import React from "react";
import { CASE_STUDY } from "../../data/site";
import { TrendingDown, TrendingUp, CheckCircle2 } from "lucide-react";

function DeltaRow({ before, after }) {
  return (
    <div className="grid grid-cols-3 items-center py-3 border-t border-slate-800 first:border-t-0">
      <div className="text-xs text-slate-400">{before.label}</div>
      <div className="font-mono text-sm text-slate-500 text-center">{before.value}</div>
      <div className="font-mono text-sm text-cyan-300 text-right">{after.value}</div>
    </div>
  );
}

export default function CaseStudy({ SectionHeader }) {
  return (
    <section id="case-study" className="relative py-24 md:py-32 px-6 md:px-10 border-t border-slate-800/50">
      <div className="max-w-[1400px] mx-auto">
        <SectionHeader
          index="09"
          kicker="Field Report"
          title="One engagement, twelve months in."
          description="An illustrative account of a single Delaware engagement — a public health agency case, generalized under NDA. It reflects our discipline of measurable outcomes, but is one of many programs we run across health, safety, judiciary, revenue, and defense workloads."
        />

        <div className="grid lg:grid-cols-12 gap-6 reveal">
          <div className="lg:col-span-5 panel corner-brackets p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <span className="pill"><span className="dot bg-emerald-400" />{CASE_STUDY.classification}</span>
              </div>
              <span className="font-mono text-[10px] text-slate-500 tracking-widest uppercase">Client · {CASE_STUDY.clientCode}</span>
            </div>
            <div className="eyebrow-muted mb-3">Situation</div>
            <p className="text-slate-300 text-sm leading-relaxed mb-8">{CASE_STUDY.summary}</p>

            <div className="eyebrow-muted mb-4">Outcomes</div>
            <ul className="space-y-3">
              {CASE_STUDY.outcomes.map((o) => (
                <li key={o} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                  <CheckCircle2 size={14} className="text-emerald-400 mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span>{o}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 pt-6 border-t border-slate-800 flex items-center justify-between">
              <div>
                <div className="eyebrow-muted mb-1">Duration</div>
                <div className="font-mono text-sm text-slate-200">12 months</div>
              </div>
              <div>
                <div className="eyebrow-muted mb-1">Waves</div>
                <div className="font-mono text-sm text-slate-200">07 / 07</div>
              </div>
              <div>
                <div className="eyebrow-muted mb-1">Status</div>
                <div className="font-mono text-sm text-emerald-400">Sustainment</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 panel p-8">
            <div className="grid grid-cols-3 mb-6">
              <div className="eyebrow-muted">Metric</div>
              <div className="eyebrow-muted text-center flex items-center justify-center gap-2">
                <TrendingDown size={12} className="text-slate-500" /> Baseline
              </div>
              <div className="eyebrow flex items-center justify-end gap-2">
                Post-migration <TrendingUp size={12} />
              </div>
            </div>
            <div>
              {CASE_STUDY.before.map((b, i) => (
                <DeltaRow key={b.label} before={b} after={CASE_STUDY.after[i]} />
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-slate-800 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="eyebrow-muted mb-2">Downtime avoided</div>
                <div className="font-mono text-xl text-slate-100">32.4h</div>
                <div className="font-mono text-[10px] text-slate-500 mt-0.5">rolling 90d</div>
              </div>
              <div>
                <div className="eyebrow-muted mb-2">Cost delta</div>
                <div className="font-mono text-xl text-emerald-400">-61.4%</div>
                <div className="font-mono text-[10px] text-slate-500 mt-0.5">annualized</div>
              </div>
              <div>
                <div className="eyebrow-muted mb-2">MTTR</div>
                <div className="font-mono text-xl text-slate-100">3.2m</div>
                <div className="font-mono text-[10px] text-slate-500 mt-0.5">from 41m</div>
              </div>
              <div>
                <div className="eyebrow-muted mb-2">POA&M closed</div>
                <div className="font-mono text-xl text-slate-100">59 / 63</div>
                <div className="font-mono text-[10px] text-slate-500 mt-0.5">two quarters</div>
              </div>
            </div>

            <div className="mt-8 border-t border-slate-800 pt-4 font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">
              Independent 3PAO verified · figures generalized under NDA
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
